import { produce } from 'immer';
import { CARD_ACTION_TYPES } from '../card/card.types';
import { computeAllCardFields } from '../../utils/firebase/computedFields';

/** Normalize and compare two collected-card variations (parallel name + print number + supply). */
function sameVariation(a, b) {
  const nameA = String(a?.cardParallelName ?? '').toUpperCase();
  const nameB = String(b?.cardParallelName ?? '').toUpperCase();
  const printA = a?.cardPrintNumber ?? null;
  const printB = b?.cardPrintNumber ?? null;
  const supplyA = a?.cardSupply ?? null;
  const supplyB = b?.cardSupply ?? null;
  return nameA === nameB && printA === printB && supplyA === supplyB;
}

export const setCardData = (cardData) => {
  return { type: CARD_ACTION_TYPES.SET_CARD_DATA, payload: cardData }
}
export const setChecklistData = (checklistData) => {
  return { type: CARD_ACTION_TYPES.SET_CHECKLIST_DATA, payload: checklistData }
}

export const appendChecklistData = (checklistData) => {
  return { type: CARD_ACTION_TYPES.APPEND_CHECKLIST_DATA, payload: checklistData }
}

export const setChecklistPagination = (pagination) => {
  return { type: CARD_ACTION_TYPES.SET_CHECKLIST_PAGINATION, payload: pagination }
}

export const setChecklistCardIds = (cardIds) => {
  return { type: CARD_ACTION_TYPES.SET_CHECKLIST_CARD_IDS, payload: cardIds }
}

export const setParallelData = (parallelData) => {
  return { type: CARD_ACTION_TYPES.SET_PARALLEL_DATA, payload: parallelData }
}

export const setChecklistMetadata = (metadata) => {
  return { type: CARD_ACTION_TYPES.SET_CHECKLIST_METADATA, payload: metadata }
}

export const addCardToQueue = (
  sport,
  year,
  manufacturer,
  series,
  cardType,
  cardTypeCategory,
  card,
  qty
) => {
  return (dispatch, getState) => {
    const state = getState();
    const queuedCards = state.card.queuedCards;
    const cardNumber = card.cardNumber;
    const { cardColor, cardParallelName, cardPrintNumber, cardSupply, cardOdds } = card.collectedCards[0];

    const newQueuedCards = produce(queuedCards, draft => {
    // Initialize totalCardCount if not present
    if (!draft.totalCardCount) {
      draft.totalCardCount = 0;
    }

    // Initialize nested structures as needed
    if (!draft[sport]) {
      draft[sport] = { totalCardCount: 0 };
    }
    if (!draft[sport][year]) {
      draft[sport][year] = { totalCardCount: 0 };
    }
    if (!draft[sport][year][manufacturer]) {
      draft[sport][year][manufacturer] = { totalCardCount: 0 };
    }
    if (!draft[sport][year][manufacturer][series]) {
      draft[sport][year][manufacturer][series] = { totalCardCount: 0 };
    }
    if (!draft[sport][year][manufacturer][series][cardType]) {
      draft[sport][year][manufacturer][series][cardType] = { totalCardCount: 0 };
    }
    if (!draft[sport][year][manufacturer][series][cardType][cardTypeCategory]) {
      draft[sport][year][manufacturer][series][cardType][cardTypeCategory] = { totalCardCount: 0 };
    }

    // Check if the card already exists in the queue
    const existingCard = draft[sport][year][manufacturer][series][cardType][cardTypeCategory][cardNumber];

    if (existingCard) {
      const incomingVariation = { cardParallelName, cardPrintNumber, cardSupply };
      const parallelIndex = existingCard.collectedCards.findIndex(v => sameVariation(v, incomingVariation));

      if (parallelIndex !== -1) {
        // If the variation exists, increment the quantity
        const newQty = existingCard.collectedCards[parallelIndex].cardQty + qty
        if (newQty <= 0) {
          existingCard.collectedCards.splice(parallelIndex, 1);
        } else {
          existingCard.collectedCards[parallelIndex].cardQty += qty;
        }
      } else {
        // If the variation doesn't exist, add it with the given quantity (use qty param, not incoming cardQty)
        existingCard.collectedCards.push(
          {
            cardParallelName: cardParallelName,
            cardColor: cardColor,
            cardQty: qty,
            cardPrintNumber: cardPrintNumber,
            cardSupply: cardSupply,
            cardOdds: cardOdds ?? null
          }
        );
      }

      // Also increment the overall card quantity
      existingCard.quantity += qty;

      if (existingCard.quantity <= 0) {
        delete draft[sport][year][manufacturer][series][cardType][cardTypeCategory][cardNumber];
      }

    } else {
      // Only set checklistPath for official categories, not custom ones
      const isCustomCategory = cardTypeCategory && cardTypeCategory.startsWith('custom:');
      const cardData = {
        ...card,
        quantity: qty,
        collectedCards: [
          {
            cardParallelName: cardParallelName,
            cardColor: cardColor,
            cardQty: qty,
            cardPrintNumber: cardPrintNumber,
            cardSupply: cardSupply,
            cardOdds: cardOdds ?? null
          }
        ]
      };
      
      // Only include checklistPath for official categories
      if (!isCustomCategory) {
        const checklistPath = `checklists/${sport}/${year}/${manufacturer}/${series}/${cardType}/${cardTypeCategory}/${cardNumber}`;
        cardData.checklistPath = checklistPath;
      }

      // If the card doesn't exist, add it with the variation and quantity
      draft[sport][year][manufacturer][series][cardType][cardTypeCategory][cardNumber] = cardData;
    }

    // Increment the total card count at each level
    draft.totalCardCount += qty;
    draft[sport].totalCardCount += qty;
    draft[sport][year].totalCardCount += qty;
    draft[sport][year][manufacturer].totalCardCount += qty;
    draft[sport][year][manufacturer][series].totalCardCount += qty;
    draft[sport][year][manufacturer][series][cardType].totalCardCount += qty;
    draft[sport][year][manufacturer][series][cardType][cardTypeCategory].totalCardCount += qty;

    if (draft[sport].totalCardCount <= 0) {
      delete draft[sport];
    } else
      if (draft[sport][year].totalCardCount <= 0) {
        delete draft[sport][year];
      } else
        if (draft[sport][year][manufacturer].totalCardCount <= 0) {
          delete draft[sport][year][manufacturer];
        } else
          if (draft[sport][year][manufacturer][series].totalCardCount <= 0) {
            delete draft[sport][year][manufacturer][series];
          } else
            if (draft[sport][year][manufacturer][series][cardType].totalCardCount <= 0) {
              delete draft[sport][year][manufacturer][series][cardType];
            } else
              if (draft[sport][year][manufacturer][series][cardType][cardTypeCategory].totalCardCount <= 0) {
                delete draft[sport][year][manufacturer][series][cardType][cardTypeCategory]
              }
    });

    dispatch({ type: CARD_ACTION_TYPES.SET_QUEUED_CARDS, payload: newQueuedCards });
  };
};

export const removeCardFromQueue = (card, parallelToRemove) => {
  return (dispatch, getState) => {
    const state = getState();
    const queuedCards = state.card.queuedCards;
    const { sport, year, manufacturer, series, cardType, cardTypeCategory, card: { cardNumber } } = card;

    const newQueuedCards = produce(queuedCards, draft => {
    // Get the existing card from the queue
    const existingCard = draft[sport][year][manufacturer][series][cardType][cardTypeCategory][cardNumber];

    if (existingCard) {
      const parallelIndex = existingCard.collectedCards.findIndex(parallel => parallel.cardParallelName === parallelToRemove);

      if (parallelIndex !== -1) {
        const qtyToRemove = existingCard.collectedCards[parallelIndex].cardQty;

        // Remove the specific variation
        existingCard.collectedCards.splice(parallelIndex, 1);

        // Adjust the total quantity of the card
        existingCard.quantity -= qtyToRemove;

        // If no variations are left, remove the card entirely
        if (existingCard.collectedCards.length === 0 || existingCard.quantity <= 0) {
          delete draft[sport][year][manufacturer][series][cardType][cardTypeCategory][cardNumber];
        }

        // Adjust the total counts
        adjustTotalCounts(draft, sport, year, manufacturer, series, cardType, cardTypeCategory, qtyToRemove);
      }
    }
    });

    dispatch({ type: CARD_ACTION_TYPES.SET_QUEUED_CARDS, payload: newQueuedCards });
  };
};

// Adjust total counts function to update counts and remove empty levels
const adjustTotalCounts = (newQueuedCards, sport, year, manufacturer, series, cardType, cardTypeCategory, qty) => {
  const levels = [
    { level: newQueuedCards[sport]?.[year]?.[manufacturer]?.[series]?.[cardType]?.[cardTypeCategory], key: cardTypeCategory },
    { level: newQueuedCards[sport]?.[year]?.[manufacturer]?.[series]?.[cardType], key: cardType },
    { level: newQueuedCards[sport]?.[year]?.[manufacturer]?.[series], key: series },
    { level: newQueuedCards[sport]?.[year]?.[manufacturer], key: manufacturer },
    { level: newQueuedCards[sport]?.[year], key: year },
    { level: newQueuedCards[sport], key: sport }
  ];

  levels.forEach(({ level, key }, idx) => {
    if (level) {
      if (level.totalCardCount === qty) {
        // If the total count matches the quantity, remove the entire level
        delete levels[idx + 1]?.level[key];
      } else {
        // Otherwise, subtract the quantity from the total count
        level.totalCardCount -= qty;
      }
    }
  });

  // Decrement the total card count in the entire collection by the card quantity
  newQueuedCards.totalCardCount -= qty;

  // Clean up any empty levels that may remain
  levels.reverse().forEach(({ level, key }, idx) => {
    if (level && Object.keys(level).length === 0) {
      if (idx < levels.length - 1) {
        delete levels[idx + 1]?.level[key];
      }
    }
  });

  // Ensure the sport level is removed if empty
  if (newQueuedCards[sport] && Object.keys(newQueuedCards[sport]).length === 1) {
    delete newQueuedCards[sport];
  }
};


export const addCardToQueueDisplay = (
  sport,
  year,
  manufacturer,
  series,
  cardType,
  cardTypeCategory,
  card,
  qty
) => {
  return (dispatch, getState) => {
    const state = getState();
    const queuedCardsDisplay = state.card.queuedCardsDisplay;
    
    const newQueuedCardsDisplay = produce(queuedCardsDisplay, draft => {
    // Find possible match
    const cardMatch = draft
      .map((queuedCard, index) => [queuedCard, index]) // Pair each card with its index
      .filter(([queuedCard]) =>
        queuedCard.sport === sport &&
        queuedCard.year === year &&
        queuedCard.manufacturer === manufacturer &&
        queuedCard.series === series &&
        queuedCard.cardType === cardType &&
        queuedCard.cardTypeCategory === cardTypeCategory &&
        queuedCard.card.cardNumber === card.cardNumber // Ensure exact match of cardNumber
      );

    let parallelIndex = -1;

    // if no matches, add new card to queue
    if (cardMatch.length === 0) {
      const cardWithQty = {
        ...card,
        collectedCards: card.collectedCards?.length
          ? card.collectedCards.map((c, i) => (i === 0 ? { ...c, cardQty: qty } : c))
          : [{ cardQty: qty }]
      };
      const newCardInfo = {
        quantity: qty,
        sport: sport,
        year: year,
        manufacturer: manufacturer,
        series: series,
        cardType: cardType,
        cardTypeCategory: cardTypeCategory,
        card: cardWithQty
      };
      draft.push(newCardInfo);
    } else {
      const incomingVariation = card.collectedCards?.[0];
      cardMatch.some(([queuedCard]) => {
        parallelIndex = queuedCard.card.collectedCards.findIndex(v => sameVariation(v, incomingVariation));
        if (parallelIndex !== -1) return true;
        return false;
      });

      if (parallelIndex !== -1) {
        const existingCard = cardMatch[0];
        const newQuantity = existingCard[0].card.collectedCards[parallelIndex].cardQty + qty;

        if (newQuantity <= 0) {
          // If the new quantity is less than or equal to zero, remove the card from the queue
          draft.splice(cardMatch[0][1], 1);
        } else {
          // Update the cardQty for the specific parallel
          existingCard[0].card.collectedCards[parallelIndex].cardQty = newQuantity;
          existingCard[0].quantity = newQuantity;
        }
      } else {
        // If no match is found, add the new card to the queue
        const cardWithQty = {
          ...card,
          collectedCards: card.collectedCards?.length
            ? card.collectedCards.map((c, i) => (i === 0 ? { ...c, cardQty: qty } : c))
            : [{ cardQty: qty }]
        };
        const newCardInfo = {
          quantity: qty,
          sport: sport,
          year: year,
          manufacturer: manufacturer,
          series: series,
          cardType: cardType,
          cardTypeCategory: cardTypeCategory,
          card: cardWithQty
        };
        draft.push(newCardInfo);
      }
    }
    });

    dispatch({ type: CARD_ACTION_TYPES.SET_QUEUED_CARDS_DISPLAY, payload: newQueuedCardsDisplay });
  };
};

export const removeCardFromQueueDisplay = (displayedIndex) => {
  return (dispatch, getState) => {
    const state = getState();
    const queuedCardsDisplay = state.card.queuedCardsDisplay;
    const originalIndex = queuedCardsDisplay.length - 1 - displayedIndex; // Map index from reversed display order
    const newQueuedCardsDisplay = produce(queuedCardsDisplay, draft => {
      draft.splice(originalIndex, 1);
    });
    dispatch({ type: CARD_ACTION_TYPES.SET_QUEUED_CARDS_DISPLAY, payload: newQueuedCardsDisplay });
  };
};

export const clearCardsFromQueue = () => {
  return { type: CARD_ACTION_TYPES.CLEAR_QUEUE }
}

export const resetCardState = () => {
  return { type: CARD_ACTION_TYPES.RESET_CARD_STATE };
}

/**
 * Sets viewing card data for a friend's collection
 * @param {string} friendUID - Friend's user ID
 * @param {Object} cardData - Friend's card data
 * @param {Object} checklistData - Friend's checklist data (optional)
 * @param {number} lastFetchedAt - Timestamp when data was fetched
 * @param {Object} collectionLastUpdated - Friend's collectionLastUpdated timestamp from Firestore
 */
export const setViewingCardData = (friendUID, cardData, checklistData = {}, lastFetchedAt = Date.now(), collectionLastUpdated = null) => {
  return {
    type: CARD_ACTION_TYPES.SET_VIEWING_CARD_DATA,
    payload: {
      friendUID,
      cardData,
      checklistData,
      lastFetchedAt,
      collectionLastUpdated
    }
  };
};

/**
 * Clears viewing card data
 */
export const clearViewingCardData = () => {
  return { type: CARD_ACTION_TYPES.CLEAR_VIEWING_CARD_DATA };
};

/**
 * Sets checklist data in cache
 * @param {string} cacheKey - Unique cache key (e.g., "checklists/baseball/2024/topps/series1/base/inserts")
 * @param {Object} data - Cache data { checklistData, checklistCardIds, metadata, parallelData }
 */
export const setChecklistCache = (cacheKey, data) => {
  return { 
    type: CARD_ACTION_TYPES.SET_CHECKLIST_CACHE, 
    payload: { cacheKey, data } 
  };
}

/**
 * Clears all checklist cache
 */
export const clearChecklistCache = () => {
  return { type: CARD_ACTION_TYPES.CLEAR_CHECKLIST_CACHE };
}

/**
 * Clears checklist cache for a specific series
 * @param {string} sport - Sport
 * @param {string} year - Year
 * @param {string} manufacturer - Manufacturer
 * @param {string} series - Series
 */
export const clearChecklistCacheBySeries = (sport, year, manufacturer, series) => {
  return { 
    type: CARD_ACTION_TYPES.CLEAR_CHECKLIST_CACHE_BY_SERIES, 
    payload: { sport, year, manufacturer, series } 
  };
}

/**
 * Bump collection invalidation key so ChecklistDisplay refetches current category collection only.
 */
export const invalidateCollection = () => {
  return { type: CARD_ACTION_TYPES.INVALIDATE_COLLECTION };
};

/**
 * Updates card quantity in Redux cardData after removal
 * @param {string} cardNumber - Card number
 * @param {string|Object} parallelIdentifier - Parallel name, or variation object { cardParallelName, cardPrintNumber?, cardSupply? } for exact match
 * @param {number} amount - Amount to remove (positive value)
 */
export const updateCardDataQuantity = (cardNumber, parallelIdentifier, amount) => {
  return (dispatch, getState) => {
    const state = getState();
    const cardData = state.card.cardData;

    // If cardData is empty or card doesn't exist, nothing to update
    if (!cardData || !cardData[cardNumber]) {
      return;
    }

    const matchVariation = (p) => {
      const name = typeof parallelIdentifier === 'object' && parallelIdentifier !== null
        ? parallelIdentifier.cardParallelName
        : parallelIdentifier;
      if (p.cardParallelName !== name) return false;
      if (typeof parallelIdentifier === 'object' && parallelIdentifier !== null) {
        if (parallelIdentifier.cardPrintNumber != null && p.cardPrintNumber !== parallelIdentifier.cardPrintNumber) return false;
        if (parallelIdentifier.cardSupply != null && p.cardSupply !== parallelIdentifier.cardSupply) return false;
      }
      return true;
    };

    const updatedCardData = produce(cardData, draft => {
      const card = draft[cardNumber];
      
      if (!card || !card.collectedCards) {
        return;
      }

      const parallelIndex = card.collectedCards.findIndex(matchVariation);

      if (parallelIndex === -1) {
        // Parallel not found, nothing to update
        return;
      }

      const parallel = card.collectedCards[parallelIndex];
      const newQuantity = parallel.cardQty - amount;

      if (newQuantity <= 0) {
        // Remove the parallel if quantity reaches 0 or below
        card.collectedCards.splice(parallelIndex, 1);
      } else {
        // Update the quantity
        parallel.cardQty = newQuantity;
      }

      // Update the card's total quantity
      const newTotalQuantity = card.collectedCards.reduce(
        (sum, p) => sum + (p.cardQty || 0), 
        0
      );
      card.quantity = newTotalQuantity;

      // Recompute optimized fields for filtering performance
      const computedFields = computeAllCardFields(card);
      Object.assign(card, computedFields);

      // If no parallels remain, we could remove the card, but it's better to keep it
      // so the UI can still show it as uncollected. The component will handle this.
    });

    dispatch({ type: CARD_ACTION_TYPES.UPDATE_CARD_DATA_QUANTITY, payload: updatedCardData });
  };
};